import {
  ArrowUturnLeftIcon,
  BellIcon,
  ExclamationCircleIcon,
  HeartIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  cancelRefundRequestById,
  remindStoreByRefundRequestId,
  reportDoubleChargeByTransaction,
  requestRefundByTransaction
} from '../../api/refund-request.api';
import {
  downloadOrderReceipt,
  receiptDocumentPath
} from '../../api/receipt.api';
import { getTransactionDetail } from '../../api/transaction.api';
import {
  BreakdownRow,
  DetailField,
  SectionCard,
  StatusPill
} from '../../components/detail/detail-parts';
import { FIELD_LABEL } from '../../components/detail/detail-classes';
import { DownloadLabel } from '../../components/detail/DownloadLabel';
import { StoreBreakdownRows } from '../../components/detail/StoreBreakdownRows';
import { AddIcNumberSheet } from '../../components/donation/AddIcNumberSheet';
import { DonationBreakdownLines } from '../../components/donation/DonationBreakdownLines';
import { Button } from '../../components/ui/button';
import {
  DONATION_NON_REFUNDABLE,
  DONATION_TONE
} from '../../constants/donation.const';
import {
  RewardBreakdown,
  TransactionDetail
} from '../../core/api/http-methods/get-user-transactions';
import {
  downloadWithToast,
  useDonationReceiptDownload
} from '../../hooks/donations/useDonationReceiptDownload';
import { useDownloadInFlight } from '../../hooks/useDownloadInFlight';
import { useScrollToTopOnOpen } from '../../hooks/useScrollToTopOnOpen';
import BackHeader from '../../shared/BackHeader';
import { formatCurrency } from '../../utils/fiat-currency.util';
import RefundActionModal from '../home/components/RecentActivities/RefundActionModal';
import { ActivityModalType } from '../home/components/RecentActivities/TransactionListItem';
import { DonationCard } from './components/DonationCard';
import { TransactionPaymentMethod } from './components/TransactionPaymentMethod';

const extractApiErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.error?.message ??
      'Something went wrong. Please try again.'
    );
  }
  return 'Something went wrong. Please try again.';
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

type ExpandedActionState = 'available' | 'requested' | 'processed';

const heroLabelByRecordType: Record<string, string> = {
  transaction: 'Total charged',
  order: 'Order',
  refund: 'Refund'
};

const getActionState = (detail: TransactionDetail): ExpandedActionState => {
  const refundRequest = detail.refund_request;
  const isProcessed =
    detail.refund_processed_for_original_transaction ||
    refundRequest?.status === 'processed' ||
    detail.record_type === 'refund';

  if (isProcessed) return 'processed';
  if (refundRequest?.status === 'pending') return 'requested';
  return 'available';
};

const getAlertConfig = (
  detail: TransactionDetail
): { show: boolean; tone: 'orange' | 'green'; message: string } => {
  const refundRequest = detail.refund_request;

  const isProcessed =
    detail.refund_processed_for_original_transaction ||
    refundRequest?.status === 'processed' ||
    detail.record_type === 'refund';

  if (isProcessed) {
    return {
      show: true,
      tone: 'green',
      message:
        'Your refund has been processed. Please allow 3-7 business days for the funds to be reflected in your account.'
    };
  }

  if (refundRequest?.status === 'pending') {
    const requestedDate = new Date(refundRequest.created_at).toLocaleDateString(
      undefined,
      { day: 'numeric', month: 'long', year: 'numeric' }
    );
    const isRefundType = refundRequest.request_type === 'refund';
    return {
      show: true,
      tone: 'orange',
      message: isRefundType
        ? `Refund requested on ${requestedDate}. The store is reviewing your request. If the store approves the refund, a new refund transaction will appear in your history.`
        : `Double charge reported on ${requestedDate}. The store is reviewing your report. If confirmed, a full refund will appear in your history.`
    };
  }

  return { show: false, tone: 'orange', message: '' };
};

const getReminderInfo = (
  detail: TransactionDetail
): { lastReminder: string; nextReminder: string } => {
  const refundRequest = detail.refund_request;
  if (!refundRequest) return { lastReminder: '', nextReminder: '' };

  const nextReminderAt = refundRequest.next_reminder_at
    ? new Date(refundRequest.next_reminder_at).getTime()
    : null;
  const shouldHideNext =
    nextReminderAt !== null && nextReminderAt <= Date.now();

  return {
    lastReminder: refundRequest.last_reminder_at
      ? `Last reminder: ${new Date(refundRequest.last_reminder_at).toLocaleString()}`
      : '',
    nextReminder:
      refundRequest.next_reminder_at && !shouldHideNext
        ? `Next reminder available after ${new Date(refundRequest.next_reminder_at).toLocaleString()}`
        : ''
  };
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const RewardsLine: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children
}) => (
  <div className="grid grid-cols-[6rem_1fr] gap-3 py-1.5">
    <p className={clsx('pt-0.5', FIELD_LABEL)}>{label}</p>
    <p className="text-sm text-gray-700">{children}</p>
  </div>
);

const RewardsCard: React.FC<{ rewards: RewardBreakdown; currency: string }> = ({
  rewards,
  currency
}) => (
  <SectionCard title="Rewards">
    <RewardsLine label="Earned">
      <span className="font-bold text-emerald-700">
        +{rewards.earned.points} pts
      </span>{' '}
      · +{formatCurrency(rewards.earned.cashback, currency)} cashback · +
      {rewards.earned.stamps} stamp{rewards.earned.stamps !== 1 ? 's' : ''}
    </RewardsLine>
    <RewardsLine label="Redeemed">
      <span className="font-bold text-red-700">
        {rewards.redeemed.points_redeemed.toLocaleString()} pts
      </span>{' '}
      ({formatCurrency(rewards.redeemed.points_redeemed_value, currency)}) · -
      {formatCurrency(rewards.redeemed.applied_discount, currency)} discounts
    </RewardsLine>
  </SectionCard>
);

const ACTION_BUTTON =
  'flex w-full items-center justify-center gap-2 rounded-2xl border border-t-1 border-gray-50 bg-white py-4 text-sm font-medium shadow-lg transition-colors';

// ─── Main Page ─────────────────────────────────────────────────────────────────

const TransactionDetailsPage: React.FC = () => {
  useScrollToTopOnOpen();
  const navigate = useNavigate();
  const { transactionId } = useParams<{ transactionId: string }>();

  const [detail, setDetail] = useState<TransactionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<ActivityModalType | null>(
    null
  );

  const requestRefundMutation = useMutation({
    mutationFn: requestRefundByTransaction
  });
  const reportDoubleChargeMutation = useMutation({
    mutationFn: reportDoubleChargeByTransaction
  });
  const remindStoreMutation = useMutation({
    mutationFn: remindStoreByRefundRequestId
  });
  const cancelRequestMutation = useMutation({
    mutationFn: cancelRefundRequestById
  });
  // A failed donation has no receipt, so the donor's tax profile is never needed for it.
  const donationReceipt = useDonationReceiptDownload(
    Boolean(detail?.donation) && detail?.donation?.status !== 'failed'
  );
  const purchaseReceiptId = detail?.transaction_id ?? detail?.id ?? '';
  const purchaseReceiptPreparing = useDownloadInFlight(
    receiptDocumentPath.order(purchaseReceiptId)
  );

  useEffect(() => {
    if (!transactionId) return;
    let cancelled = false;

    setLoading(true);
    setError(null);

    getTransactionDetail(transactionId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load transaction details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [transactionId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
        <p className="text-sm text-gray-500">
          {error ?? 'Transaction not found.'}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-sm font-medium text-orange-600 hover:underline"
        >
          Go Back
        </button>
      </div>
    );
  }

  const actionState = getActionState(detail);
  const alertConfig = getAlertConfig(detail);
  const reminderInfo = getReminderInfo(detail);

  const disableRemindStore =
    Boolean(detail.refund_request?.next_reminder_at) &&
    new Date(String(detail.refund_request?.next_reminder_at)).getTime() >
      Date.now();

  const formattedDate = dayjs(detail.date).format('MMM DD, YYYY, hh:mm A');

  const currency = detail.currency ?? 'MYR';
  const pb = detail.payment_breakdown;
  const rb = detail.reward_breakdown;
  const donation = detail.donation ?? null;
  // Only a charge's paid donation was charged with it; one processing or failed was not.
  const paidDonation =
    detail.record_type === 'transaction' && donation?.status === 'paid'
      ? donation
      : null;
  const refundable = pb.total - (pb.refunded_amount ?? 0);
  const totalCharged = pb.total + (paidDonation?.amount ?? 0);

  const handleDownloadOrderReceipt = () =>
    downloadWithToast(() => downloadOrderReceipt(purchaseReceiptId));

  const handleRequestRefund = () => setActiveModal('requestRefund');
  const handleReportDoubleCharge = () => setActiveModal('reportDoubleCharge');
  const handleCloseModal = () => setActiveModal(null);

  const handleConfirmModalAction = async () => {
    if (!activeModal || !detail.transaction_id) return;
    try {
      if (activeModal === 'requestRefund') {
        await requestRefundMutation.mutateAsync(detail.transaction_id);
        toast.success('Refund requested successfully.');
      } else {
        await reportDoubleChargeMutation.mutateAsync(detail.transaction_id);
        toast.success('Double charge reported successfully.');
      }
      // Refresh detail to reflect new refund_request
      const updated = await getTransactionDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      toast.error(extractApiErrorMessage(err));
    } finally {
      handleCloseModal();
    }
  };

  const handleRemindStore = async () => {
    const refundRequestId = detail.refund_request?.refund_request_id;
    if (!refundRequestId) return;
    try {
      await remindStoreMutation.mutateAsync(refundRequestId);
      toast.success('Store reminded successfully.');
      const updated = await getTransactionDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      toast.error(extractApiErrorMessage(err));
    }
  };

  const handleCancelRequest = async () => {
    const refundRequestId = detail.refund_request?.refund_request_id;
    if (!refundRequestId) return;
    try {
      await cancelRequestMutation.mutateAsync(refundRequestId);
      toast.success('Refund request cancelled.');
      const updated = await getTransactionDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      toast.error(extractApiErrorMessage(err));
    }
  };

  const isModalPending =
    requestRefundMutation.isPending || reportDoubleChargeMutation.isPending;
  const isRemindPending = remindStoreMutation.isPending;
  const isCancelPending = cancelRequestMutation.isPending;

  // A charge that moved no money has nothing to give back, so neither action applies. This is
  // a real row: a bill that rewards covered in full settles as a successful 0.00 charge, and it
  // passes every other condition here. The backend refuses such a request too — this keeps the
  // buttons from promising something it would reject.
  const hasRefundableAmount = Number(pb?.total ?? 0) > 0;

  const showActionButtons =
    actionState !== 'processed' &&
    !detail.is_partially_refunded &&
    detail.record_type === 'transaction' &&
    detail.status === 'success' &&
    hasRefundableAmount;

  return (
    <div className="min-h-screen bg-white">
      <BackHeader />

      {/* ── Hero section ── */}
      <div className="mx-auto mt-4 flex max-w-2xl flex-col items-center bg-white px-4 pb-4 pt-4 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-600">
          {heroLabelByRecordType[detail.record_type] ?? 'Total charged'}
        </p>
        <p className="mt-2 text-3xl font-semibold text-orange-500">
          {formatCurrency(totalCharged, currency)}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          {detail.store_name} · {formattedDate}
        </p>
        {paidDonation && (
          <p
            className={clsx(
              'mt-1 flex items-center gap-1 text-xs',
              DONATION_TONE.text
            )}
          >
            <HeartIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            Includes{' '}
            {formatCurrency(paidDonation.amount, paidDonation.currency)}{' '}
            donation to {paidDonation.charity.name}
          </p>
        )}
        <StatusPill status={detail.status} className="mt-3" />

        {/* Alert banner */}
        {alertConfig.show && (
          <div
            className={clsx(
              'mt-4 w-full rounded-xl px-4 py-3 text-left text-sm',
              alertConfig.tone === 'orange'
                ? 'bg-orange-50 text-orange-700'
                : 'bg-emerald-50 text-emerald-700'
            )}
          >
            {alertConfig.message}
          </div>
        )}
      </div>

      {/* ── Cards ── */}
      <div className="mx-auto max-w-2xl space-y-3 px-4 py-4 pb-10">
        <SectionCard
          title={
            detail.record_type === 'order'
              ? 'Order Breakdown'
              : 'Payment Breakdown'
          }
        >
          <StoreBreakdownRows
            breakdown={pb}
            currency={currency}
            storeName={detail.store_name}
          />
          {pb.refunded_amount != null && pb.refunded_amount > 0 && (
            <BreakdownRow
              label="Refunded"
              value={`- ${formatCurrency(pb.refunded_amount, currency)}`}
              valueClassName="text-red-600"
            />
          )}
          {paidDonation && (
            <DonationBreakdownLines
              charityName={paidDonation.charity.name}
              amount={paidDonation.amount}
              currency={paidDonation.currency}
              note={DONATION_NON_REFUNDABLE}
              totalLabel="Total charged"
              total={totalCharged}
            />
          )}
        </SectionCard>

        {donation && (
          <DonationCard
            donation={donation}
            onDownloadReceipt={() =>
              donationReceipt.download(
                donation.donation_ref,
                donation.deductible_for_donor
              )
            }
          />
        )}

        <SectionCard title="Order details" aside={detail.store_name}>
          <DetailField
            label="Order ID"
            value={detail.order_id}
            copyValue={detail.order_id}
          />
          {detail.transaction_id && (
            <DetailField
              label="Transaction ID"
              value={detail.transaction_id}
              copyValue={detail.transaction_id}
            />
          )}

          {/* Payment Method (renders nothing when there is none) */}
          <TransactionPaymentMethod
            paymentMethod={detail.payment_method}
            display={detail.payment_method_display}
          />

          {detail.purchase_receipt_available && (
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadOrderReceipt}
              disabled={purchaseReceiptPreparing}
              aria-busy={purchaseReceiptPreparing}
              className="mt-3 h-12 w-full rounded-xl border-orange-500 text-sm font-semibold text-orange-600 hover:bg-orange-50 hover:text-orange-600"
            >
              <DownloadLabel pending={purchaseReceiptPreparing}>
                Download receipt (PDF)
              </DownloadLabel>
            </Button>
          )}
        </SectionCard>

        {rb && <RewardsCard rewards={rb} currency={currency} />}

        {/* Reminder timestamps */}
        {(reminderInfo.lastReminder || reminderInfo.nextReminder) && (
          <div className="space-y-1 px-1 pt-3 text-sm italic text-gray-500">
            {reminderInfo.lastReminder && <p>{reminderInfo.lastReminder}</p>}
            {reminderInfo.nextReminder && <p>{reminderInfo.nextReminder}</p>}
          </div>
        )}

        {/* ── Action buttons ── */}
        {showActionButtons && (
          <div className="space-y-3 pt-3">
            {actionState === 'available' ? (
              <>
                <div>
                  <button
                    type="button"
                    onClick={handleRequestRefund}
                    className={clsx(
                      ACTION_BUTTON,
                      'text-orange-600 hover:bg-orange-50'
                    )}
                  >
                    <ArrowUturnLeftIcon className="h-5 w-5" />
                    Request Refund
                  </button>
                  <p className="mt-2 text-center text-xs text-gray-500">
                    Refundable: up to {formatCurrency(refundable, currency)} (
                    {detail.store_name})
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleReportDoubleCharge}
                  className={clsx(
                    ACTION_BUTTON,
                    'text-orange-600 hover:bg-orange-50'
                  )}
                >
                  <ExclamationCircleIcon className="h-5 w-5" />
                  Report Double Charge
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={disableRemindStore || isRemindPending}
                  onClick={handleRemindStore}
                  className={clsx(
                    ACTION_BUTTON,
                    disableRemindStore || isRemindPending
                      ? 'cursor-not-allowed text-gray-400'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <BellIcon className="h-5 w-5" />
                  {isRemindPending ? 'Reminding...' : 'Remind Store'}
                </button>
                <button
                  type="button"
                  disabled={isCancelPending}
                  onClick={handleCancelRequest}
                  className={clsx(
                    ACTION_BUTTON,
                    isCancelPending
                      ? 'cursor-not-allowed text-orange-500 opacity-60'
                      : 'text-orange-500 hover:bg-orange-50'
                  )}
                >
                  <XMarkIcon className="h-5 w-5" />
                  {isCancelPending ? 'Cancelling...' : 'Cancel Request'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <AddIcNumberSheet {...donationReceipt.sheet} />

      {activeModal !== null && detail.transaction_id && (
        <RefundActionModal
          activeModal={activeModal}
          amount={String(pb.total)}
          currency={currency}
          isPending={isModalPending}
          onClose={handleCloseModal}
          onConfirm={handleConfirmModalAction}
        />
      )}
    </div>
  );
};

export default TransactionDetailsPage;
